import { useEffect, useCallback } from "react";
const DEFAULT_MOVE_STEP = 5;
export const useKeyboardShortcuts = ({ svgCanvasInstanceRef, svgCanvasRef, onDelete, moveStep = DEFAULT_MOVE_STEP, }) => {
    const handleKeyDown = useCallback((e) => {
        const canvas = svgCanvasInstanceRef.current;
        const containerElement = svgCanvasRef.current;
        if (!canvas || !containerElement)
            return;
        const selectedElements = canvas.getSelectedElements();
        if (selectedElements.length === 0)
            return;
        if (e.key === "Delete" || e.key === "Backspace") {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }
            e.preventDefault();
            onDelete();
            return;
        }
        if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }
            e.preventDefault();
            let deltaX = 0;
            let deltaY = 0;
            switch (e.key) {
                case "ArrowUp":
                    deltaY = -moveStep;
                    break;
                case "ArrowDown":
                    deltaY = moveStep;
                    break;
                case "ArrowLeft":
                    deltaX = -moveStep;
                    break;
                case "ArrowRight":
                    deltaX = moveStep;
                    break;
            }
            if (deltaX !== 0 || deltaY !== 0) {
                // Try to use canvas's moveSelectedElements if available
                if (typeof canvas.moveSelectedElements === "function") {
                    canvas.moveSelectedElements(deltaX, deltaY, false);
                }
                else {
                    // Fallback: manually move elements
                    selectedElements.forEach((elem) => {
                        if (elem instanceof SVGElement) {
                            const transform = elem.getAttribute("transform") || "";
                            const translateMatch = transform.match(/translate\(([^)]+)\)/);
                            let currentX = 0;
                            let currentY = 0;
                            if (translateMatch) {
                                const coords = translateMatch[1].split(/[,\s]+/).map(Number);
                                currentX = coords[0] || 0;
                                currentY = coords[1] || 0;
                            }
                            const newX = currentX + deltaX;
                            const newY = currentY + deltaY;
                            let newTransform = transform.replace(/translate\([^)]+\)/, "").trim();
                            if (newTransform && !newTransform.endsWith(" ")) {
                                newTransform += " ";
                            }
                            newTransform += `translate(${newX},${newY})`;
                            elem.setAttribute("transform", newTransform.trim());
                        }
                        // Update position attributes for elements that use them
                        const xAttr = elem.getAttribute("x");
                        const yAttr = elem.getAttribute("y");
                        const x1Attr = elem.getAttribute("x1");
                        const y1Attr = elem.getAttribute("y1");
                        const x2Attr = elem.getAttribute("x2");
                        const y2Attr = elem.getAttribute("y2");
                        if (xAttr !== null) {
                            elem.setAttribute("x", (parseFloat(xAttr) + deltaX).toString());
                        }
                        if (yAttr !== null) {
                            elem.setAttribute("y", (parseFloat(yAttr) + deltaY).toString());
                        }
                        if (x1Attr !== null) {
                            elem.setAttribute("x1", (parseFloat(x1Attr) + deltaX).toString());
                        }
                        if (y1Attr !== null) {
                            elem.setAttribute("y1", (parseFloat(y1Attr) + deltaY).toString());
                        }
                        if (x2Attr !== null) {
                            elem.setAttribute("x2", (parseFloat(x2Attr) + deltaX).toString());
                        }
                        if (y2Attr !== null) {
                            elem.setAttribute("y2", (parseFloat(y2Attr) + deltaY).toString());
                        }
                    });
                    canvas.call("changed", selectedElements);
                }
            }
        }
    }, [svgCanvasInstanceRef, svgCanvasRef, onDelete, moveStep]);
    useEffect(() => {
        const containerElement = svgCanvasRef.current;
        if (!containerElement)
            return;
        const handleDocumentKeyDown = (e) => {
            const canvas = svgCanvasInstanceRef.current;
            if (!canvas)
                return;
            const selectedElements = canvas.getSelectedElements();
            if (selectedElements.length === 0)
                return;
            // Only handle if the container or its children have focus, or if no input is focused
            const activeElement = document.activeElement;
            const isInputFocused = activeElement instanceof HTMLInputElement ||
                activeElement instanceof HTMLTextAreaElement ||
                (activeElement === null || activeElement === void 0 ? void 0 : activeElement.tagName) === "INPUT" ||
                (activeElement === null || activeElement === void 0 ? void 0 : activeElement.tagName) === "TEXTAREA";
            if (isInputFocused && (e.key === "Delete" || e.key === "Backspace" || e.key.startsWith("Arrow"))) {
                return;
            }
            handleKeyDown(e);
        };
        document.addEventListener("keydown", handleDocumentKeyDown);
        return () => {
            document.removeEventListener("keydown", handleDocumentKeyDown);
        };
    }, [svgCanvasRef, svgCanvasInstanceRef, handleKeyDown]);
};
